import type { PushPlatform } from '../types';
import { objectFields } from './object-fields';

/** The device a push test was last sent to from this workspace. */
export type LastTestedRecipient = {
  /** The device token. */
  id: string;
  platform: PushPlatform | null;
  /** Present only when that test was sent to the provider's sandbox. */
  sandbox?: boolean;
};

const KEY_PREFIX = 'cio:last-send-test-recipient:push:env-';

/** Where a workspace's remembered device token is kept. */
export function lastTestedRecipientKey(workspaceId: number): string {
  return `${KEY_PREFIX}${String(workspaceId)}`;
}

function toPlatform(value: unknown): PushPlatform | null {
  return value === 'ios' || value === 'android' ? value : null;
}

function toLastTestedRecipient(value: unknown): LastTestedRecipient | null {
  const fields = objectFields(value);

  const id = fields.get('id');
  if (typeof id !== 'string') {
    return null;
  }

  const recipient: LastTestedRecipient = { id, platform: toPlatform(fields.get('platform')) };

  const sandbox = fields.get('sandbox');
  if (typeof sandbox === 'boolean') {
    recipient.sandbox = sandbox;
  }

  return recipient;
}

/** Read back a remembered recipient, or null when the stored value is unusable. */
export function parseLastTestedRecipient(raw: string): LastTestedRecipient | null {
  try {
    return toLastTestedRecipient(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * The recipient remembered for this workspace, or null when there is none.
 * Storage can be unavailable (privacy mode), which reads as "nothing remembered".
 */
export function readLastTestedRecipient(workspaceId: number): LastTestedRecipient | null {
  try {
    const raw = window.localStorage.getItem(lastTestedRecipientKey(workspaceId));
    return raw == null ? null : parseLastTestedRecipient(raw);
  } catch {
    return null;
  }
}

/** Remember a recipient for this workspace. Best effort: failing to store is not an error. */
export function writeLastTestedRecipient(workspaceId: number, recipient: LastTestedRecipient): void {
  try {
    window.localStorage.setItem(lastTestedRecipientKey(workspaceId), JSON.stringify(recipient));
  } catch {
    // The send already succeeded; only the convenience of remembering it is lost.
  }
}

type LastTestedMatch = {
  recipient: string;
  platform: PushPlatform | null;
  sandbox: boolean;
};

/**
 * Whether the form currently holds exactly what was last tested — the same
 * token, on the same device type, sent the same way.
 */
export function matchesLastTestedRecipient(lastTested: LastTestedRecipient | null, current: LastTestedMatch): boolean {
  if (lastTested == null) {
    return false;
  }
  return (
    lastTested.id === current.recipient &&
    lastTested.platform === current.platform &&
    sameSandbox(lastTested.sandbox, current.sandbox)
  );
}

/** A recipient stored before the sandbox was ever recorded matches either way. */
function sameSandbox(stored: boolean | undefined, sandbox: boolean): boolean {
  return stored === undefined || stored === sandbox;
}
